import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, TrendingUp, CheckCircle, Plus, Eye, Download, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Colores para gráficos (compatibles con ambos temas)
const CHART_COLORS = [
  'hsl(217, 91%, 60%)',  // Azul principal (primary)
  'hsl(199, 89%, 48%)',  // Info/Celeste
  'hsl(217, 91%, 45%)',  // Azul más oscuro
  'hsl(199, 89%, 60%)',  // Celeste más claro
  'hsl(217, 91%, 70%)'   // Azul más claro
];

const ESTADO_COLORS = {
  'Borrador': 'hsl(215, 20%, 65%)',
  'Enviada': 'hsl(217, 91%, 60%)',
  'Adjudicada': 'hsl(158, 64%, 39%)'
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [vehiculos, setVehiculos] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [revendedores, setRevendedores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, vehiculosRes, ofertasRes, revendedoresRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/vehiculos`),
        axios.get(`${API}/ofertas`),
        axios.get(`${API}/revendedores?includeDeleted=true`),
      ]);
      setStats(statsRes.data);
      setVehiculos(vehiculosRes.data);
      setOfertas(ofertasRes.data);
      setRevendedores(revendedoresRes.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  // Calcular KPIs
  const kpis = stats ? [
    {
      title: 'Publicaciones Activas',
      value: stats.publicaciones_activas,
      icon: FileText,
      testId: 'kpi-publicaciones-activas'
    },
    {
      title: 'Ofertas Pendientes',
      value: stats.ofertas_pendientes,
      icon: TrendingUp,
      testId: 'kpi-ofertas-nuevas'
    },
    {
      title: 'Tasa de Adjudicación',
      value: `${stats.tasa_adjudicacion}%`,
      icon: CheckCircle,
      testId: 'kpi-tasa-adjudicacion'
    },
    {
      title: 'Ofertas Totales',
      value: stats.total_ofertas,
      icon: DollarSign,
      testId: 'kpi-ofertas-totales'
    },
  ] : [];

  // Calcular datos para gráficas
  let marcasData = [];
  let revendedoresTopCantidad = [];
  let revendedoresTopMonto = [];

  if (vehiculos.length > 0 && ofertas.length > 0 && revendedores.length > 0) {
    // Marcas más adjudicadas
    const marcasAdjudicadas = {};
    const adjudicados = vehiculos.filter(v => v.estadoPublicacion === 'Adjudicada');
    adjudicados.forEach(v => {
      marcasAdjudicadas[v.marca] = (marcasAdjudicadas[v.marca] || 0) + 1;
    });
    marcasData = Object.entries(marcasAdjudicadas)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    // Revendedores que más compran
    const revendedoresCompras = {};
    const revendedoresMap = {};
    revendedores.forEach(r => { revendedoresMap[r.id] = r; });

    const ofertasAceptadas = ofertas.filter(o => o.estado === 'Aceptada');
    ofertasAceptadas.forEach(o => {
      const rev = revendedoresMap[o.revendedorId];
      if (rev) {
        if (!revendedoresCompras[rev.nombre]) {
          revendedoresCompras[rev.nombre] = { cantidad: 0, monto: 0 };
        }
        revendedoresCompras[rev.nombre].cantidad += 1;
        revendedoresCompras[rev.nombre].monto += o.monto;
      }
    });

    revendedoresTopCantidad = Object.entries(revendedoresCompras)
      .map(([name, data]) => ({ name, value: data.cantidad }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    revendedoresTopMonto = Object.entries(revendedoresCompras)
      .map(([name, data]) => ({ name, value: data.monto }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }

  const quickActions = [
    {
      label: 'Nueva Publicación',
      href: '/admin/publicaciones/nueva',
      icon: Plus,
      variant: 'default',
      testId: 'dashboard-new-listing-button'
    },
    {
      label: 'Ver Ofertas',
      href: '/admin/ofertas',
      icon: Eye,
      variant: 'outline',
      testId: 'dashboard-view-offers-button'
    },
    {
      label: 'Reportes',
      href: '/admin/reportes',
      icon: Download,
      variant: 'outline',
      testId: 'dashboard-reports-button'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  // Calcular tendencias (simuladas)
  const getTrendData = () => {
    return [
      { trend: '+12.5%', isPositive: true },
      { trend: '+8.3%', isPositive: true },
      { trend: '+5.2%', isPositive: true },
      { trend: '-3.1%', isPositive: false }
    ];
  };

  const trends = getTrendData();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Header */}
      <div className="bg-card/50 border-b border-border py-8 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
                Dashboard de Análisis
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Monitoreo en tiempo real de operaciones
              </p>
            </div>
            <div className="hidden md:flex items-center gap-3">
              {quickActions.slice(0, 2).map((action) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.label}
                    asChild
                    size="sm"
                    className="bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20"
                  >
                    <Link to={action.href}>
                      <Icon className="h-4 w-4 mr-2" />
                      {action.label}
                    </Link>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Section */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {kpis.map((kpi, index) => {
            const Icon = kpi.icon;
            const trend = trends[index];
            return (
              <Card
                key={kpi.title}
                className="relative overflow-hidden bg-card border border-border hover:border-border/80 transition-all duration-300 shadow-lg"
                data-testid={kpi.testId}
              >
                <div className="absolute top-0 right-0 w-24 h-24 opacity-[0.03]">
                  <Icon className="w-full h-full text-primary" />
                </div>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {kpi.title}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-end justify-between">
                    <div className="text-3xl font-bold text-foreground">{kpi.value}</div>
                    {trend && (
                      <div className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md ${trend.isPositive
                          ? 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'
                          : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                        }`}>
                        <span>{trend.isPositive ? '↑' : '↓'}</span>
                        <span>{trend.trend}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">vs. mes anterior</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts Grid */}
        <div className="mt-6 grid gap-5 grid-cols-1 lg:grid-cols-12">
          {/* Marcas más adjudicadas - Span 7 columns */}
          {marcasData.length > 0 && (
            <Card className="lg:col-span-7 bg-card border border-border shadow-xl">
              <CardHeader className="pb-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground">Marcas Más Adjudicadas</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Distribución por marca de vehículos</p>
                  </div>
                  <div className="p-2 bg-primary/10 rounded-lg border border-primary/20">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {marcasData.slice(0, 5).map((item, index) => {
                    const maxValue = Math.max(...marcasData.slice(0, 5).map(d => d.value));
                    const percentage = (item.value / maxValue) * 100;
                    return (
                      <div key={index} className="group">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg font-bold text-white text-xs border"
                              style={{
                                background: `linear-gradient(135deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`,
                                borderColor: `${CHART_COLORS[index]}40`
                              }}>
                              {index + 1}
                            </div>
                            <span className="font-semibold text-foreground">{item.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground">{percentage.toFixed(0)}%</span>
                            <span className="font-bold text-foreground text-base">{item.value}</span>
                          </div>
                        </div>
                        <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                          <div
                            className="absolute left-0 top-0 h-full rounded-full transition-all duration-700 ease-out group-hover:opacity-90"
                            style={{
                              width: `${percentage}%`,
                              background: `linear-gradient(90deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Distribución de Estados - Circular Chart - Span 5 columns */}
          {vehiculos.length > 0 && (
            <Card className="lg:col-span-5 bg-card border border-border shadow-xl">
              <CardHeader className="pb-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground">Estado de Publicaciones</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Distribución actual</p>
                  </div>
                  <div className="p-2 bg-muted rounded-lg">
                    <TrendingUp className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {(() => {
                    const estados = {};
                    vehiculos.forEach(v => {
                      estados[v.estadoPublicacion] = (estados[v.estadoPublicacion] || 0) + 1;
                    });
                    const estadosArray = Object.entries(estados).map(([name, value]) => ({ name, value }));
                    const total = vehiculos.length;

                    return estadosArray.map((item, index) => {
                      const percentage = (item.value / total) * 100;
                      const color = ESTADO_COLORS[item.name] || CHART_COLORS[index];
                      return (
                        <div key={index} className="group">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: color }}
                              />
                              <span className="text-sm font-semibold text-foreground">{item.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{percentage.toFixed(1)}%</span>
                              <span className="font-bold text-foreground">{item.value}</span>
                            </div>
                          </div>
                          <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="absolute left-0 top-0 h-full rounded-full transition-all duration-700"
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: color
                              }}
                            />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revendedores que más compran (cantidad) - Span 6 */}
          {revendedoresTopCantidad.length > 0 && (
            <Card className="lg:col-span-6 bg-card border border-border shadow-xl">
              <CardHeader className="pb-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground">Top Revendedores</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Por cantidad de compras</p>
                  </div>
                  <div className="p-2 bg-muted rounded-lg">
                    <CheckCircle className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {revendedoresTopCantidad.slice(0, 5).map((item, index) => {
                    const maxValue = Math.max(...revendedoresTopCantidad.slice(0, 5).map(d => d.value));
                    const percentage = (item.value / maxValue) * 100;
                    return (
                      <div key={index} className="group relative">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white shadow-md"
                                style={{
                                  background: `linear-gradient(135deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                                }}>
                                {item.value}
                              </div>
                            </div>
                            <div>
                              <div className="font-semibold text-foreground text-sm">{item.name}</div>
                              <div className="text-xs text-muted-foreground">Compras realizadas</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-semibold text-foreground">{percentage.toFixed(0)}%</div>
                            <div className="text-xs text-muted-foreground">del total</div>
                          </div>
                        </div>
                        <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="absolute left-0 top-0 h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${percentage}%`,
                              background: `linear-gradient(90deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revendedores top por monto - Span 6 */}
          {revendedoresTopMonto.length > 0 && (
            <Card className="lg:col-span-6 bg-card border border-border shadow-xl">
              <CardHeader className="pb-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground">Inversión Destacada</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Monto total invertido</p>
                  </div>
                  <div className="p-2 bg-muted rounded-lg">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {revendedoresTopMonto.slice(0, 5).map((item, index) => {
                    const maxValue = Math.max(...revendedoresTopMonto.slice(0, 5).map(d => d.value));
                    const percentage = (item.value / maxValue) * 100;
                    return (
                      <div key={index} className="group relative">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white shadow-md text-xs"
                                style={{
                                  background: `linear-gradient(135deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                                }}>
                                ${(item.value / 1000).toFixed(0)}K
                              </div>
                            </div>
                            <div>
                              <div className="font-semibold text-foreground text-sm">{item.name}</div>
                              <div className="text-xs text-muted-foreground">${item.value.toLocaleString('en-US')}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-semibold text-foreground">{percentage.toFixed(0)}%</div>
                            <div className="text-xs text-muted-foreground">del total</div>
                          </div>
                        </div>
                        <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="absolute left-0 top-0 h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${percentage}%`,
                              background: `linear-gradient(90deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;